import React from 'react';
import { useDimensionsRef } from 'hooks';
import { useChartColors } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { useDailyOvertimeDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/store/dailyOvertimeDashboard.store';
import {
  DONUT_MIN_SIZE,
  DONUT_INNER_RATIO,
  DONUT_FILL_RATIO,
  TEAM_MEMBER_TYPE_COLORS,
} from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/DailyOvertimeDashboard.constants';
import {
  DonutChart,
  DonutChartItem,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/DonutChart';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/DailyOvertimeDashboard.styled';
import {
  ChartContainer,
  DonutSizer,
  LegendContainer,
  LegendItem,
  LegendDot,
  LegendLabel,
  LegendValue,
} from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/OtByTeamMemberTypeChart.styled';

function getTypeColor(type: string, index: number, fallbackColors: string[]): string {
  return TEAM_MEMBER_TYPE_COLORS[type] || fallbackColors[index % fallbackColors.length];
}

export const OtByTeamMemberTypeChart: React.FC = () => {
  const data = useDailyOvertimeDashboardStore((s) => s.overtimeByTeamMemberType);
  const palette = useChartColors();
  const [donutAreaRef, { width: donutAreaWidth, height: donutAreaHeight }] =
    useDimensionsRef<HTMLDivElement>();

  if (!data || data.length === 0) {
    return <EmptyStateContainer>No data available for the selected filters.</EmptyStateContainer>;
  }

  const items: DonutChartItem[] = data.map((d, i) => ({
    label: d.type,
    value: d.overtimeHours,
    color: getTypeColor(d.type, i, palette.chartColors),
    ariaLabel: `${d.type}: ${d.overtimeHours}h (${d.percentage}%)`,
    tooltipHtml: `<strong>${d.type}</strong><br/>Overtime: ${d.overtimeHours}h<br/>Share: ${d.percentage}%`,
  }));

  const donutSize = Math.max(
    DONUT_MIN_SIZE,
    Math.round(Math.min(donutAreaWidth, donutAreaHeight) * DONUT_FILL_RATIO) || DONUT_MIN_SIZE,
  );

  return (
    <ChartContainer>
      <DonutSizer ref={donutAreaRef}>
        <DonutChart items={items} size={donutSize} innerRadiusRatio={DONUT_INNER_RATIO} />
      </DonutSizer>
      <LegendContainer>
        {data.map((d, i) => (
          <LegendItem key={d.type}>
            <LegendDot $color={getTypeColor(d.type, i, palette.chartColors)} />
            <LegendLabel>{d.type}</LegendLabel>
            <LegendValue>
              {d.overtimeHours}h ({d.percentage}%)
            </LegendValue>
          </LegendItem>
        ))}
      </LegendContainer>
    </ChartContainer>
  );
};
